import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  Users, 
  Calendar,
  CalendarDays,
  FileText,
  Home,
  Inbox,
  FolderOpen,
  HardHat,
  Briefcase,
  Archive,
  StickyNote,
  Wrench,
  TrendingUp
} from 'lucide-react';

// Şube Paneli bileşenlerini import et - aynı görünüm için
import BranchDashboard from '../branch/BranchDashboard';
import BranchEmployees from '../branch/BranchEmployees';
import BranchAttendance from '../branch/BranchAttendance';
import BranchAssignments from '../branch/BranchAssignments';
import BranchCalendar from '../branch/BranchCalendar';
import BranchReports from '../branch/BranchReports';
import BranchSubcontractors from '../branch/BranchSubcontractors';
import BranchServices from '../branch/BranchServices';
import BranchJobs from '../branch/BranchJobs';
import BranchSettings from '../branch/BranchSettings';
import BranchCommunication from '../branch/BranchCommunication';
import BranchFiles from '../branch/BranchFiles';
import BranchWall from '../branch/BranchWall';

const API = process.env.REACT_APP_BACKEND_URL;

const BoardSubeYonetimContent = () => {
  const { branchId } = useParams();
  const { user } = useAuth();
  const { getBadgeCount, markSeen } = useBadges();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [branchName, setBranchName] = useState('');
  const [loading, setLoading] = useState(true);

  // Badge counts
  const [unseenJobsCount, setUnseenJobsCount] = useState(0);
  const [unseenRequestsCount, setUnseenRequestsCount] = useState(0);
  const [subcontractorNewCount, setSubcontractorNewCount] = useState(0);

  // Şube adını al
  useEffect(() => {
    const fetchBranchName = async () => {
      try {
        const token = localStorage.getItem('fortis_token');
        const response = await fetch(`${API}/api/branches`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const branches = await response.json();
          const branch = branches.find(b => b.id === branchId);
          setBranchName(branch?.name || 'Şube');
        }
      } catch (err) {
        console.error('Şube adı alınamadı:', err);
        setBranchName('Şube');
      } finally {
        setLoading(false);
      }
    };
    fetchBranchName();
  }, [branchId]);

  // Badge sayılarını çek
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Görülmemiş iş sayısı
      const jobsRes = await fetch(`${API}/api/branch/jobs/unseen-count?branch_id=${branchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (jobsRes.ok) {
        const data = await jobsRes.json();
        setUnseenJobsCount(data.unseen_count || 0);
      }

      // Görülmemiş talep sayısı
      const reqRes = await fetch(`${API}/api/branch/requests/unseen-count?branch_id=${branchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setUnseenRequestsCount(data.unseen_count || 0);
      }

      // Taşeron yeni kayıt sayısı
      const subRes = await fetch(`${API}/api/branch/subcontractors/new-counts?branch_id=${branchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (subRes.ok) {
        const data = await subRes.json();
        setSubcontractorNewCount(data.total_new || 0);
      }
    } catch (error) {
      console.error('Badge sayıları alınamadı:', error);
    }
  }, [branchId]);

  useEffect(() => {
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchBadgeCounts]);

  // Menu Items - BranchPanel ile AYNI
  const menuItems = [
    { key: 'dashboard', icon: Home, label: 'Ana Sayfa', badge: 0 },
    { key: 'employees', icon: Users, label: 'Şube Çalışanları', badge: 0 },
    { key: 'attendance', icon: Calendar, label: 'Mesai Takibi', badge: 0 },
    { key: 'assignments', icon: TrendingUp, label: 'İş Programı', badge: 0 },
    { key: 'calendar', icon: CalendarDays, label: 'Takvim', badge: 0 },
    { key: 'reports', icon: FileText, label: 'Raporlar', badge: 0 },
    { key: 'subcontractors', icon: HardHat, label: 'Taşeronlar', badge: subcontractorNewCount },
    { key: 'services', icon: Wrench, label: 'Alınan Hizmetler', badge: 0 },
    { key: 'jobs', icon: Briefcase, label: 'İşler', badge: unseenJobsCount + unseenRequestsCount },
    { key: 'info', icon: FolderOpen, label: 'Şube Bilgileri', badge: 0 },
    { key: 'communication', icon: Inbox, label: 'İletişim', badge: 0 },
    { key: 'files', icon: Archive, label: 'Dosyalar', badge: 0 },
    { key: 'wall', icon: StickyNote, label: 'Şube Duvarı', badge: 0 },
  ];

  // İçerik render - Şube Paneli bileşenlerini doğrudan kullan
  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return <BranchDashboard branchIdOverride={branchId} readOnly={true} />;
      case 'employees':
        return <BranchEmployees branchIdOverride={branchId} readOnly={true} />;
      case 'attendance':
        return <BranchAttendance branchIdOverride={branchId} readOnly={true} />;
      case 'assignments':
        return <BranchAssignments branchIdOverride={branchId} readOnly={true} />;
      case 'calendar':
        return <BranchCalendar branchIdOverride={branchId} readOnly={true} />;
      case 'reports':
        return <BranchReports branchIdOverride={branchId} readOnly={true} />;
      case 'subcontractors':
        return <BranchSubcontractors branchIdOverride={branchId} readOnly={true} />;
      case 'services':
        return <BranchServices branchIdOverride={branchId} readOnly={true} />;
      case 'jobs':
        return <BranchJobs branchIdOverride={branchId} readOnly={true} />;
      case 'info':
        return <BranchSettings branchIdOverride={branchId} readOnly={true} />;
      case 'communication':
        return <BranchCommunication branchIdOverride={branchId} readOnly={true} />;
      case 'files':
        return <BranchFiles branchIdOverride={branchId} readOnly={true} />;
      case 'wall':
        return <BranchWall branchIdOverride={branchId} readOnly={true} />;
      default:
        return <BranchDashboard branchIdOverride={branchId} readOnly={true} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="board-sube-yonetim">
      {/* Header - BranchPanel ile aynı stil */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/70 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-8 w-8 text-amber-600" />
              <span className="text-xl font-bold text-amber-600">{branchName} - Şube Yönetim</span>
            </div>
            <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-300">Salt Okunur</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - BranchPanel ile AYNI */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => setActiveMenu(item.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    activeMenu === item.key
                      ? 'bg-amber-600 text-white'
                      : 'hover:bg-amber-50 text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge > 0 && (
                    <Badge className={`ml-auto ${
                      activeMenu === item.key ? 'bg-white text-amber-600' : 'bg-red-500 text-white'
                    }`}>
                      {item.badge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content - Şube Paneli bileşenlerini göster */}
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

const BoardSubeYonetim = () => {
  return (
    <BadgeProvider panel="branch">
      <BoardSubeYonetimContent />
    </BadgeProvider>
  );
};

export default BoardSubeYonetim;
